import {
  CheckCircleIcon,
  IdentificationCardIcon,
  MapPinIcon,
  PhoneIcon,
} from "@phosphor-icons/react";
import { Input } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { useAuthStore } from "@/client/stores/auth";
import { useResumeStore } from "@/client/stores/resume";

import { CV_COUNTRIES, findCountryByCode, findCountryByName } from "./basics/cv-countries";
import { PhoneInput } from "./basics/phone-input";
import { PhotoLanguagePanel } from "./basics/photo-language-panel";
import { SmartCombobox } from "./shared/smart-combobox";

const splitName = (name: string) => {
  const [firstName = "", ...rest] = name.trim().split(/\s+/).filter(Boolean);
  return { firstName, lastName: rest.join(" ") };
};

const FieldLabel = ({
  children,
  optional = false,
}: {
  children: ReactNode;
  optional?: boolean;
}) => (
  <label className="mb-1.5 flex items-center gap-2 text-[11px] font-bold text-indigo-950">
    {children}
    {optional && (
      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[8px] font-semibold text-slate-500">
        Facultatif
      </span>
    )}
  </label>
);

const FieldCard = ({
  title,
  description,
  icon: Icon,
  children,
}: {
  title: string;
  description: string;
  icon: typeof IdentificationCardIcon;
  children: ReactNode;
}) => (
  <section className="rounded-2xl border border-indigo-100 bg-white p-3.5 shadow-[0_10px_30px_rgba(30,27,75,0.05)] sm:p-4">
    <header className="flex items-center gap-2.5 border-b border-slate-100 pb-3">
      <span className="grid size-8 place-items-center rounded-lg bg-indigo-50 text-indigo-700">
        <Icon size={18} weight="duotone" />
      </span>
      <div>
        <h2 className="text-xs font-bold text-indigo-950 sm:text-sm">{title}</h2>
        <p className="mt-0.5 text-[10px] text-slate-500">{description}</p>
      </div>
    </header>
    <div className="mt-4 grid gap-3.5 sm:grid-cols-2">{children}</div>
  </section>
);

export const BasicsSection = () => {
  const basics = useResumeStore((state) => state.resume.data.basics);
  const setValue = useResumeStore((state) => state.setValue);
  const initialName = useMemo(() => splitName(basics.name ?? ""), []);
  const [firstName, setFirstName] = useState(initialName.firstName);
  const [lastName, setLastName] = useState(initialName.lastName);
  const selectedCountry = findCountryByName(basics.country ?? "");
  const accountCountry = findCountryByCode(useAuthStore((state) => state.user?.countryCode));
  const prefilledCountry = useRef(false);

  // Un champ Pays vide à l'ouverture prend le pays du compte ; un choix fait dans le CV est conservé.
  useEffect(() => {
    if (prefilledCountry.current || !accountCountry) return;
    prefilledCountry.current = true;
    if (!basics.country?.trim()) setValue("basics.country", accountCountry.name);
  }, [accountCountry, basics.country, setValue]);

  const completion = [
    basics.name,
    basics.headline,
    basics.email,
    basics.phone,
    basics.location,
    basics.country,
  ].filter((value) => value?.trim()).length;

  const updateName = (first: string, last: string) =>
    setValue("basics.name", [first.trim(), last.trim()].filter(Boolean).join(" "));
  const countryOptions = CV_COUNTRIES.map((country) => ({
    value: country.name,
    label: country.name,
    prefix: country.flag,
    keywords: [country.code, country.dialCode],
  }));

  return (
    <div className="mx-auto w-full max-w-[720px] space-y-4 pb-3">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-600">
            Coordonnées
          </p>
          <h1 className="mt-1.5 text-2xl font-bold tracking-tight text-indigo-950 sm:text-[28px]">
            Présentez-vous simplement
          </h1>
          <p className="mt-1.5 max-w-lg text-xs leading-5 text-slate-500 sm:text-sm">
            Ajoutez les informations utiles pour être identifié et contacté.
          </p>
        </div>
        <div className="w-full rounded-xl border border-indigo-100 bg-indigo-50/60 p-3 sm:w-[150px]">
          <div className="flex justify-between text-[10px] font-semibold text-indigo-950/65">
            <span>Progression</span>
            <strong className="text-indigo-700">{Math.round((completion / 6) * 100)} %</strong>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white">
            <div
              className="h-full rounded-full bg-gradient-to-r from-indigo-600 to-emerald-500 transition-[width]"
              style={{ width: `${(completion / 6) * 100}%` }}
            />
          </div>
        </div>
      </header>

      <PhotoLanguagePanel />

      <FieldCard
        title="Identité professionnelle"
        description="Le nom affiché en haut de votre CV."
        icon={IdentificationCardIcon}
      >
        <div>
          <FieldLabel>Prénom</FieldLabel>
          <Input
            value={firstName}
            autoComplete="given-name"
            placeholder="Jerry"
            onChange={(event) => {
              setFirstName(event.target.value);
              updateName(event.target.value, lastName);
            }}
            className="min-h-10 rounded-xl bg-white text-xs"
          />
        </div>
        <div>
          <FieldLabel>Nom</FieldLabel>
          <Input
            value={lastName}
            autoComplete="family-name"
            placeholder="Obama"
            onChange={(event) => {
              setLastName(event.target.value);
              updateName(firstName, event.target.value);
            }}
            className="min-h-10 rounded-xl bg-white text-xs"
          />
        </div>
        <div className="sm:col-span-2">
          <FieldLabel>Titre professionnel</FieldLabel>
          <Input
            value={basics.headline ?? ""}
            maxLength={60}
            placeholder="Ex. Développeur Frontend"
            onChange={(event) => setValue("basics.headline", event.target.value)}
            className="min-h-10 rounded-xl bg-white text-xs"
          />
          <p className="mt-1 text-right text-[9px] text-slate-400">
            {basics.headline?.length ?? 0}/60
          </p>
        </div>
        <div className="sm:col-span-2">
          <FieldLabel optional>Accroche</FieldLabel>
          <textarea
            value={basics.tagline ?? ""}
            rows={3}
            maxLength={200}
            placeholder="Ex. À la recherche d’un stage pour développer mes compétences"
            onChange={(event) => setValue("basics.tagline", event.target.value)}
            className={cn(
              "flex w-full resize-none rounded-xl border border-border bg-white px-3 py-2 text-xs",
              "hover:bg-secondary/20 transition-colors placeholder:opacity-80",
              "focus:border-primary focus:bg-secondary/20 focus-visible:outline-none",
            )}
          />
          <p className="mt-1 text-right text-[9px] text-slate-400">
            {basics.tagline?.length ?? 0}/200
          </p>
        </div>
      </FieldCard>

      <FieldCard
        title="Contact"
        description="Des coordonnées professionnelles et à jour."
        icon={PhoneIcon}
      >
        <div>
          <FieldLabel>Adresse e-mail</FieldLabel>
          <Input
            value={basics.email ?? ""}
            type="email"
            autoComplete="email"
            placeholder="jerry@email.com"
            onChange={(event) => setValue("basics.email", event.target.value)}
            className="min-h-10 rounded-xl bg-white text-xs"
          />
        </div>
        <div>
          <FieldLabel>Téléphone</FieldLabel>
          <PhoneInput
            value={basics.phone ?? ""}
            preferredCountry={selectedCountry ?? accountCountry}
            onChange={(value) => setValue("basics.phone", value)}
          />
        </div>
      </FieldCard>

      <FieldCard
        title="Localisation"
        description="Une ville et un pays suffisent."
        icon={MapPinIcon}
      >
        <div>
          <FieldLabel>Ville</FieldLabel>
          <Input
            value={basics.location ?? ""}
            autoComplete="address-level2"
            placeholder="Votre ville"
            onChange={(event) => setValue("basics.location", event.target.value)}
            className="min-h-10 rounded-xl bg-white text-xs"
          />
        </div>
        <div>
          <FieldLabel>Pays</FieldLabel>
          <SmartCombobox
            value={basics.country ?? ""}
            options={countryOptions}
            placeholder="Rechercher un pays"
            onChange={(value) => setValue("basics.country", value)}
          />
        </div>
        <div className="sm:col-span-2">
          <FieldLabel optional>Adresse</FieldLabel>
          <Input
            value={basics.address ?? ""}
            autoComplete="street-address"
            placeholder="Quartier, rue ou adresse"
            onChange={(event) => setValue("basics.address", event.target.value)}
            className="min-h-10 rounded-xl bg-white text-xs"
          />
        </div>
      </FieldCard>

      <div className="flex items-start gap-2 rounded-xl border border-emerald-100 bg-emerald-50/60 px-3 py-2.5">
        <CheckCircleIcon size={17} weight="fill" className="mt-0.5 shrink-0 text-emerald-500" />
        <p className="text-[10px] font-medium leading-4 text-emerald-800 sm:text-[11px]">
          Les modifications sont enregistrées automatiquement et visibles dans l’aperçu.
        </p>
      </div>
    </div>
  );
};
